import {ChevronDownIcon, ChevronRightIcon} from "lucide-react";
import {useState} from "react";

import {Badge} from "@components/ui/badge";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@components/ui/collapsible";
import {Tooltip, TooltipContent, TooltipTrigger} from "@components/ui/tooltip";
import {cn} from "@lib/utils";

import type {RevenueTieOutCell, RevenueTieOutData, RevenueTieOutRow} from "@hooks/use-vnext";

type TieOutStatus = RevenueTieOutCell["status"];

interface DisplayTieOutRow {
    id: string;
    label: string;
    account_code: string;
    in_grid: boolean;
    months: Record<string, RevenueTieOutCell>;
    total_variance: number;
}

function formatMonthKey(mk: string): string {
    const [y, m] = mk.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(undefined, {month: "short", year: "2-digit"});
}

function formatVariance(value: number): string {
    if (Math.abs(value) < 0.005) return "$0";
    const abs = Math.abs(value).toLocaleString(undefined, {maximumFractionDigits: 0});
    return `${value > 0 ? "+" : "-"}$${abs}`;
}

function formatAmount(value: number): string {
    return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function joinNotes(cells: RevenueTieOutCell[]): string | undefined {
    const notes = Array.from(new Set(cells.map((cell) => cell.note).filter((note): note is string => Boolean(note?.trim()))));
    return notes.length > 0 ? notes.join("\n") : undefined;
}

const CELL_TEXT_STYLES: Record<TieOutStatus, string> = {
    green: "text-emerald-600",
    amber: "text-amber-700 font-medium",
    red: "text-rose-700 font-semibold",
    ledger_only: "text-slate-500",
};

const STATUS_RANK: Record<TieOutStatus, number> = {
    ledger_only: -1,
    green: 0,
    amber: 1,
    red: 2,
};

function strongestStatus(cells: RevenueTieOutCell[]): TieOutStatus {
    return cells.reduce<TieOutStatus>(
        (status, cell) => (STATUS_RANK[cell.status] > STATUS_RANK[status] ? cell.status : status),
        "green",
    );
}

function roundedSum(values: number[]): number {
    return Math.round(values.reduce((sum, value) => sum + value, 0) * 100) / 100;
}

function buildGroupedRow(
    id: string,
    label: string,
    rows: RevenueTieOutRow[],
    tieOut: RevenueTieOutData,
    ledgerOnly = false,
): DisplayTieOutRow {
    const months = Object.fromEntries(
        tieOut.months.map((month) => {
            const cells = rows.map((row) => row.months[month.month_key]).filter((cell): cell is RevenueTieOutCell => Boolean(cell));
            const accountValue = roundedSum(cells.map((cell) => cell.account_value));
            const bottomUp = roundedSum(cells.map((cell) => cell.bottom_up));
            const variance = roundedSum(cells.map((cell) => cell.variance));
            return [
                month.month_key,
                {
                    account_value: accountValue,
                    bottom_up: bottomUp,
                    variance,
                    status: ledgerOnly ? ("ledger_only" as TieOutStatus) : strongestStatus(cells),
                    note: joinNotes(cells),
                },
            ];
        }),
    );

    return {
        id,
        label,
        account_code: rows.length === 1 ? rows[0].account_code : "",
        in_grid: true,
        months,
        total_variance: roundedSum(rows.map((row) => row.total_variance)),
    };
}

function isLedgerOnlyRow(row: DisplayTieOutRow | RevenueTieOutRow, months: RevenueTieOutData["months"]): boolean {
    return months.some((month) => row.months[month.month_key]?.status === "ledger_only");
}

function isClientFeesRow(row: RevenueTieOutRow): boolean {
    return row.label.trim().toLowerCase() === "client fees";
}

function VarianceCell({cell, monthStatus}: {cell: RevenueTieOutCell | undefined; monthStatus?: string}) {
    if (!cell) {
        return <td className="h-7 min-w-[54px] border-l border-slate-100 px-1.5 py-1 text-right text-[11px] text-slate-300">-</td>;
    }

    const isLedgerOnly = cell.status === "ledger_only";
    const displayValue = isLedgerOnly ? formatAmount(cell.account_value) : formatVariance(cell.variance);
    const detailText = isLedgerOnly
        ? `Ledger amount ${formatAmount(cell.account_value)}`
        : `Account row ${formatAmount(cell.account_value)} | Bottom-up ${formatAmount(cell.bottom_up)}`;
    const content = (
        <span className={CELL_TEXT_STYLES[cell.status]}>
            {displayValue}
        </span>
    );

    return (
        <td
            className={cn(
                "h-7 min-w-[54px] border-l border-slate-100 px-1.5 py-1 text-right text-[11px] tabular-nums",
                monthStatus === "forecast" && "bg-sky-50/20",
            )}
            title={cell.note ? undefined : detailText}
        >
            {cell.note ? (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex max-w-full cursor-help items-center justify-end rounded-sm border-b border-dotted border-current bg-transparent p-0 text-right tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1"
                            aria-label={`${displayValue}. ${cell.note}`}
                        >
                            {content}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64 whitespace-pre-line bg-slate-900 text-left text-white">
                        <p>{cell.note}</p>
                        <p className="mt-1 text-[10px] text-slate-300">{detailText}</p>
                    </TooltipContent>
                </Tooltip>
            ) : content}
        </td>
    );
}

function TieOutRow({
    row,
    months,
    isDetail = false,
    isExpandable = false,
    isExpanded = false,
    onToggle,
}: {
    row: DisplayTieOutRow | RevenueTieOutRow;
    months: RevenueTieOutData["months"];
    isDetail?: boolean;
    isExpandable?: boolean;
    isExpanded?: boolean;
    onToggle?: () => void;
}) {
    const isLedger = isLedgerOnlyRow(row, months);
    const totalStatus: TieOutStatus = isLedger ? "ledger_only" : Math.abs(row.total_variance) <= 1 ? "green" : "red";
    const totalLedgerAmount = roundedSum(
        months.map((month) => row.months[month.month_key]?.account_value ?? 0),
    );
    const label = (
        <div className={cn("flex min-w-0 items-center gap-1", isDetail && "pl-6")}>
            {isExpandable && (
                <ChevronRightIcon className={cn("size-3.5 shrink-0 text-slate-400 transition-transform", isExpanded && "rotate-90")} />
            )}
            <span className={cn("truncate", isDetail ? "text-slate-600" : "font-medium text-slate-800")}>{row.label}</span>
            {row.account_code ? <span className="shrink-0 text-[10px] text-slate-400">{row.account_code}</span> : null}
            {!row.in_grid && !isLedger && <span className="shrink-0 text-[10px] text-amber-600">no detail</span>}
        </div>
    );

    return (
        <tr className={cn("h-7 border-b border-slate-100 last:border-0", isDetail && "bg-slate-50/40")}>
            <td className="sticky left-0 z-10 h-7 min-w-[190px] max-w-[190px] bg-inherit py-1 pr-3 text-xs">
                {isExpandable ? (
                    <button
                        type="button"
                        className="flex w-full min-w-0 items-center text-left"
                        aria-expanded={isExpanded}
                        onClick={onToggle}
                    >
                        {label}
                    </button>
                ) : label}
            </td>
            {months.map((month) => (
                <VarianceCell key={month.month_key} cell={row.months[month.month_key]} monthStatus={month.status} />
            ))}
            <td className="h-7 min-w-[60px] border-l border-slate-200 px-1.5 py-1 text-right text-[11px] tabular-nums">
                <span className={CELL_TEXT_STYLES[totalStatus]}>
                    {isLedger ? formatAmount(totalLedgerAmount) : formatVariance(row.total_variance)}
                </span>
            </td>
        </tr>
    );
}

export function WorkbenchRevenueTieOutSection({tieOut}: {tieOut: RevenueTieOutData | null | undefined}) {
    const [isOpen, setIsOpen] = useState(false);
    const [otherOpen, setOtherOpen] = useState(false);
    const [ledgerOpen, setLedgerOpen] = useState(false);

    if (!tieOut || tieOut.rows.length === 0) return null;

    const clientFeesRows = tieOut.rows.filter(isClientFeesRow);
    const otherRows = tieOut.rows.filter((row) => !isClientFeesRow(row) && row.in_grid);
    const ledgerRows = tieOut.rows.filter((row) => !isClientFeesRow(row) && !row.in_grid);
    const clientFeesGroup = clientFeesRows.length > 0 ? buildGroupedRow("client-fees", "Client Fees", clientFeesRows, tieOut) : null;
    const otherGroup = otherRows.length > 0 ? buildGroupedRow("other-revenue", "Other Revenue", otherRows, tieOut) : null;
    const ledgerGroup =
        ledgerRows.length > 0 ? buildGroupedRow("ledger-only", "Ledger-only Revenue", ledgerRows, tieOut, true) : null;
    const cellCounts = tieOut.rows.flatMap((row) => Object.values(row.months)).reduce(
        (acc, cell) => {
            acc[cell.status] = (acc[cell.status] ?? 0) + 1;
            return acc;
        },
        {} as Record<string, number>,
    );
    const isFullyReconciled = !cellCounts.amber && !cellCounts.red;

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <section className="rounded-lg border border-slate-200 bg-white">
                <CollapsibleTrigger asChild>
                    <button className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-left">
                        <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-[1.5px] text-slate-400">Check Figures</p>
                            <h3 className="mt-0.5 text-sm font-semibold text-slate-900">Revenue Tie-Out</h3>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            {isFullyReconciled ? (
                                <Badge className="h-6 bg-emerald-100 px-2 text-[11px] text-emerald-700 hover:bg-emerald-100">All green</Badge>
                            ) : (
                                <Badge className="h-6 bg-rose-100 px-2 text-[11px] text-rose-700 hover:bg-rose-100">
                                    {cellCounts.red ?? 0} red / {cellCounts.amber ?? 0} amber
                                </Badge>
                            )}
                            <ChevronDownIcon className={cn("size-4 text-slate-500 transition-transform", isOpen && "rotate-180")} />
                        </div>
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-3">
                    <div className="overflow-x-auto rounded-md border border-slate-100">
                        <table className="w-max min-w-full border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                                    <th className="sticky left-0 z-10 min-w-[190px] max-w-[190px] bg-slate-50 py-1.5 pr-3 font-medium">Line</th>
                                    {tieOut.months.map((m) => (
                                        <th key={m.month_key} className={cn("min-w-[54px] border-l border-slate-100 px-1.5 py-1.5 text-right font-medium", m.status === "forecast" && "text-sky-600")}>
                                            {formatMonthKey(m.month_key)}
                                        </th>
                                    ))}
                                    <th className="min-w-[60px] border-l border-slate-200 px-1.5 py-1.5 text-right font-medium">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientFeesGroup && <TieOutRow row={clientFeesGroup} months={tieOut.months} />}
                                {otherGroup && (
                                    <TieOutRow
                                        row={otherGroup}
                                        months={tieOut.months}
                                        isExpandable
                                        isExpanded={otherOpen}
                                        onToggle={() => setOtherOpen((open) => !open)}
                                    />
                                )}
                                {otherOpen && otherRows.map((row) => (
                                    <TieOutRow key={`${row.account_node_id}:${row.label}`} row={row} months={tieOut.months} isDetail />
                                ))}
                                {ledgerGroup && (
                                    <TieOutRow
                                        row={ledgerGroup}
                                        months={tieOut.months}
                                        isExpandable
                                        isExpanded={ledgerOpen}
                                        onToggle={() => setLedgerOpen((open) => !open)}
                                    />
                                )}
                                {ledgerOpen && ledgerRows.map((row) => (
                                    <TieOutRow key={`${row.account_node_id}:${row.label}`} row={row} months={tieOut.months} isDetail />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CollapsibleContent>
            </section>
        </Collapsible>
    );
}
